import React, { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import Row, { AutoRow } from 'components/Layout/Row'
import { useTranslation } from 'contexts/Localization'
import { format } from 'date-fns'
import { Box, Button, Text } from 'vvs-uikit'
import { ModalActions } from 'components/Modal'
import boostPools from 'config/constants/boostPools'
import { formatLockPeriod, getEndDate } from 'views/xVvs/helpers'
import xVvsContext from 'views/xVvs/context'
import BigNumber from 'bignumber.js'
import { getBalanceAmount } from 'utils/formatBalance'
import tokens from 'config/constants/tokens'
import { StyledText } from 'views/xVvs/styles'
import LockingPeriodSelect from './LockingPeriodSelect'
import Warning from '../Warning'
import DepositAmount from '../DepositAmount'

interface SelectPeriodStepProps {
  currentPid: number
  amount: BigNumber
}

const StyledSeperator = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  color: ${({ theme }) => (theme.isDark ? theme.colors.darkGrey : theme.colors.text)};

  &::before {
    content: '';
    height: 1px;
    width: 100%;
    display: block;
    background: ${({ theme }) => theme.colors.background};
    margin-right: 14px;
    margin-top: 4px;
  }

  &::after {
    content: '';
    height: 1px;
    width: 100%;
    display: block;
    background: ${({ theme }) => theme.colors.background};
    margin-left: 14px;
    margin-top: 4px;
  }
`

const StyledRow = styled(Row)`
  flex-wrap: wrap;

  ${({ theme }) => theme.mediaQueries.sm} {
    flex-wrap: nowrap;
  }
`

const SelectPeriodStep = ({ currentPid, amount }: SelectPeriodStepProps) => {
  const { t } = useTranslation()
  const { goNext, selectLockPeriod, state } = useContext(xVvsContext)
  const { selectedPool } = state
  const avaiablePeriods = useMemo(() => boostPools.filter((x) => x.pid > currentPid), [currentPid])
  const currentPool = boostPools.find((x) => x.pid === currentPid)
  const endDate = getEndDate(selectedPool?.lockPeriod)

  const formattedAmount = getBalanceAmount(amount, tokens.xvvs.decimals)

  useEffect(() => {
    selectLockPeriod(avaiablePeriods[0].pid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avaiablePeriods])

  return (
    <Box>
      <Text color="textSubtle" textAlign="center">
        {t('Select a new locking period')}
      </Text>
      <Text small mt={4}>
        {t('UPGRADE TO')}
      </Text>
      <StyledRow justify="space-between" mt={3}>
        {avaiablePeriods.map((pool) => (
          <LockingPeriodSelect
            key={pool.pid}
            {...pool}
            isSelected={selectedPool?.pid === pool.pid}
            onSelect={() => selectLockPeriod(pool.pid)}
          />
        ))}
      </StyledRow>
      <AutoRow justify="space-between" mt={3}>
        <StyledText small>{`${t('Current Vault')} (${formatLockPeriod(currentPool.lockPeriod)} ${t(
          'Lock',
        )})`}</StyledText>
        <StyledText small>{currentPool?.multiplier / 100}x</StyledText>
      </AutoRow>
      <AutoRow justify="space-between" mt={1}>
        <StyledText small>{`${t('Target Vault')} (${formatLockPeriod(selectedPool?.lockPeriod)} ${t(
          'Lock',
        )})`}</StyledText>
        <Text small>{selectedPool?.multiplier / 100}x</Text>
      </AutoRow>
      <AutoRow justify="space-between" mt={1}>
        <StyledText small>{`${t('End')}`}</StyledText>
        <Text small>{format(endDate, 'MMM dd, yyyy')}</Text>
      </AutoRow>
      <StyledSeperator mt={4} mb={4}>
        +
      </StyledSeperator>
      <Text small>{t("You'll lock")}</Text>
      <DepositAmount amount={formattedAmount} />
      <Text small color="textSubtle" maxWidth="468px" mt={3} mb={3}>
        {t(
          'This amount included unlocked xVVS that you may withdraw. Upgrading the vault will lock this amount as well.',
        )}
      </Text>
      <Warning />
      <ModalActions>
        <Button width="100%" onClick={goNext}>
          {t('Upgrade')}
        </Button>
      </ModalActions>
    </Box>
  )
}

export default SelectPeriodStep
